import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { buildEmployeeHistory, buildEmployeeTimeline } from "../services/attendanceAnalyticsService";

function Stat({ label, value }) {
  return (
    <Box sx={{ minWidth: 130 }}>
      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>{label}</Typography>
      <Typography variant="h6" fontWeight={800}>{value}</Typography>
    </Box>
  );
}

function typeIcon(type) {
  if (type === "IN") return <LoginIcon fontSize="small" color="success" />;
  if (type === "OUT") return <LogoutIcon fontSize="small" color="error" />;
  return <CompareArrowsIcon fontSize="small" color="disabled" />;
}

/**
 * Timeline + 30-day history for a single employee, built from loaded rows.
 */
export default function EmployeeTimelineDialog({ open, onClose, rows, employeeId }) {
  const timeline = useMemo(
    () => (open && employeeId ? buildEmployeeTimeline(rows || [], employeeId) : []),
    [open, rows, employeeId]
  );
  const history = useMemo(
    () => (open && employeeId ? buildEmployeeHistory(rows || [], employeeId) : null),
    [open, rows, employeeId]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Employee {employeeId} — Timeline &amp; History</DialogTitle>
      <DialogContent dividers>
        {history && (
          <>
            <Typography variant="subtitle2" gutterBottom>Last {history.windowDays} days</Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Stat label="Total Punches" value={history.totalPunches} />
              <Stat label="Active Days" value={history.activeDays} />
              <Stat label="Late Count" value={history.lateCount} />
              <Stat label="Avg First Punch" value={history.averageFirstPunch} />
              <Stat label="Avg Last Punch" value={history.averageLastPunch} />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {history.machinesUsed.map((m) => <Chip key={`m_${m}`} size="small" variant="outlined" label={`Machine: ${m}`} />)}
              {history.branchesUsed.map((b) => <Chip key={`b_${b}`} size="small" variant="outlined" color="primary" label={`Branch: ${b}`} />)}
            </Stack>
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        <Typography variant="subtitle2" gutterBottom>Punch Timeline ({timeline.length})</Typography>
        {timeline.length === 0 ? (
          <Typography color="text.secondary">No punches found for this employee in the loaded data.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {timeline.map((p) => (
              <Stack key={p.id} direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
                {typeIcon(p.type)}
                <Typography sx={{ minWidth: 64, fontWeight: 700 }}>{p.time}</Typography>
                <Chip size="small" label={p.type} color={p.type === "IN" ? "success" : p.type === "OUT" ? "error" : "default"} variant="outlined" />
                <Typography variant="body2" color="text.secondary">{p.date}</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">{p.branch} · {p.device}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
